import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faBan,
  faCalendar,
  faCheck,
  faComments,
  faCubes,
  faDatabase,
  faEye,
  faFileLines,
  faFlask,
  faPaperPlane,
  faRotate,
  faRotateLeft,
  faServer,
  faShieldHalved,
  faTrashCan,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import { setNotification } from '../../redux/notificationSlice'
import { useRequest } from '../../hooks/UseRequest'
import {
  FormatDateTimeToSame,
  FormatISOToAZDate,
} from '../../helpers/formatting'
import { useLeadFunnelBlocksPermissions } from './lead_funnel_blocks/useLeadFunnelBlocksPermissions'
import { useBlockChangeRequestApi } from './lead_funnel_blocks/useBlockChangeRequestApi'
import { useAuthReady } from './lead_funnel_blocks/useAuthReady'
import {
  groupCardsByTreatment,
  reviewActions,
  statusMeta,
} from './lead_funnel_blocks/blockChangeHelpers'
import LayoutSidebar from '../../components/LayoutSidebar'
import Loading from '../../components/Loading'
import ChangeRequestChangesTable from './lead_funnel_blocks/ChangeRequestChangesTable'
import ChangeRequestValidation, {
  ValidationPills,
} from './lead_funnel_blocks/ChangeRequestValidation'
import ChangeRequestDiscussion from './lead_funnel_blocks/ChangeRequestDiscussion'
import {
  ApproveDialog,
  CommentDialog,
  ConfirmDialog,
} from './lead_funnel_blocks/ChangeRequestDialogs'

// `rank` only orders the buttons: secondary, then primary, then destructive last.
const ACTION_BUTTONS = {
  edit: {
    label: 'Edit in Explorer',
    className: '',
    icon: faArrowUpRightFromSquare,
    rank: 0,
  },
  retry_cache_clear: {
    label: 'Retry cache clear',
    className: '',
    icon: faRotate,
    rank: 0,
  },
  request_changes: {
    label: 'Request changes',
    className: 'is-warning is-light',
    icon: faRotateLeft,
    rank: 0,
  },
  submit: {
    label: 'Submit for review',
    className: 'is-primary',
    icon: faPaperPlane,
    rank: 1,
  },
  approve: {
    label: 'Approve and apply',
    className: 'is-primary',
    icon: faCheck,
    rank: 1,
  },
  discard: {
    label: 'Discard',
    className: 'is-danger is-outlined',
    icon: faTrashCan,
    rank: 2,
  },
  reject: {
    label: 'Reject',
    className: 'is-danger is-outlined',
    icon: faBan,
    rank: 2,
  },
}

const BLOCKS_BASE = () =>
  `${process.env.REACT_APP_API_DOMAIN}/lead_funnel/blocks`
const DATE_TIME = 'MM/dd/yyyy hh:mm a'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// DATETIME columns arrive in Arizona time without a zone; created_at is a UTC timestamp.
function azDateTime(value) {
  if (!value) return '—'
  return FormatDateTimeToSame(value, DATE_TIME)
}

function collectTreatmentNames(nodes, names = {}) {
  nodes.forEach((node) => {
    names[node.id] = node.name
    collectTreatmentNames(node.children, names)
  })
  return names
}

function SummaryItem({ icon, label, children }) {
  return (
    <div className="lox-cr-review__summary-item">
      <FontAwesomeIcon icon={icon} className="lox-cr-review__summary-icon" />
      <div>
        <dt>{label}</dt>
        <dd>{children}</dd>
      </div>
    </div>
  )
}

function ChangeRequestSummary({
  changeRequest,
  cards,
  groups,
  validation,
  isValidated,
}) {
  const blocks = new Set(
    cards.map(
      (card) =>
        `${card.table}:${
          card.target_id ?? card.target_temp_ref ?? card.temp_ref
        }`
    )
  )
  const reviewed = changeRequest.reviewed_by_user_name
    ? `${changeRequest.reviewed_by_user_name} · ${azDateTime(
        changeRequest.reviewed_at
      )}`
    : '—'
  let cacheCleared = '—'
  if (changeRequest.status === 'applied') {
    cacheCleared = changeRequest.cache_cleared_at
      ? azDateTime(changeRequest.cache_cleared_at)
      : 'Pending'
  }

  return (
    <dl className="lox-cr-review__summary" data-testid="cr-summary">
      <SummaryItem icon={faFileLines} label="Changes">
        {plural(cards.length, 'change')}
      </SummaryItem>
      <SummaryItem icon={faFlask} label="Treatments">
        {plural(groups.length, 'treatment')}
      </SummaryItem>
      <SummaryItem icon={faCubes} label="Blocks">
        {plural(blocks.size, 'block')}
      </SummaryItem>
      <SummaryItem icon={faShieldHalved} label="Validation">
        {isValidated ? (
          <>
            <span
              className={validation.errors.length > 0 ? 'has-text-danger' : ''}
            >
              {plural(validation.errors.length, 'error')}
            </span>
            {' · '}
            <span
              className={
                validation.warnings.length > 0
                  ? 'lox-cr-review__warning-text'
                  : ''
              }
            >
              {plural(validation.warnings.length, 'warning')}
            </span>
          </>
        ) : (
          'Not validated'
        )}
      </SummaryItem>
      <SummaryItem icon={faUser} label="Author">
        {changeRequest.created_by_user_name ?? 'unknown'}
      </SummaryItem>
      <SummaryItem icon={faCalendar} label="Created">
        {changeRequest.created_at
          ? FormatISOToAZDate(changeRequest.created_at, DATE_TIME)
          : '—'}
      </SummaryItem>
      <SummaryItem icon={faPaperPlane} label="Submitted">
        {azDateTime(changeRequest.submitted_at)}
      </SummaryItem>
      <SummaryItem icon={faEye} label="Reviewed">
        {reviewed}
      </SummaryItem>
      <SummaryItem icon={faDatabase} label="Applied">
        {azDateTime(changeRequest.applied_at)}
      </SummaryItem>
      <SummaryItem icon={faServer} label="DS cache cleared">
        {cacheCleared}
      </SummaryItem>
    </dl>
  )
}

const COMMENT_DIALOGS = {
  request_changes: {
    title: 'Request changes',
    confirmLabel: 'Request changes',
  },
  reject: { title: 'Reject', confirmLabel: 'Reject' },
}

export default function LeadFunnelBlockChangeRequest() {
  const { permissions, currentUserId, isSpoofing } =
    useLeadFunnelBlocksPermissions()
  const { id } = useParams()
  const navi = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const ready = useAuthReady()
  const api = useBlockChangeRequestApi()
  const [detail, setDetail] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [dialog, setDialog] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [tree, setTree] = useState(null)
  const [vocabulary, setVocabulary] = useState(null)
  const treeRequest = useRequest()
  const vocabularyRequest = useRequest()

  const currentId = useRef(id)
  currentId.current = id

  useEffect(() => {
    setDetail(null)
    setDialog(null)
    setLoadFailed(false)
  }, [id])

  useEffect(() => {
    if (!ready) return
    let isStale = false
    setLoadFailed(false)
    api.detail(id).then((data) => {
      if (isStale) return
      if (data) setDetail(data)
      else setLoadFailed(true)
    })
    return () => {
      isStale = true
    }
    // useBlockChangeRequestApi() recreates its methods every render; they do not belong here.
  }, [ready, id, loadAttempt])

  // Names only: without them the table falls back to treatment ids and action numbers.
  useEffect(() => {
    if (!ready) return
    treeRequest.setEndpoint(`${BLOCKS_BASE()}/treatments`)
    treeRequest
      .get()
      .then((res) => setTree(res?.data?.data ?? null))
      .catch(() => setTree(null))
    vocabularyRequest.setEndpoint(`${BLOCKS_BASE()}/vocabulary`)
    vocabularyRequest
      .get()
      .then((res) => setVocabulary(res?.data?.data ?? null))
      .catch(() => setVocabulary(null))
    // useRequest() recreates its methods every render; they do not belong here.
  }, [ready])

  const nodes = tree?.nodes ?? []
  const treatmentNamesById = useMemo(() => collectTreatmentNames(nodes), [tree])
  const actionLabels = useMemo(
    () =>
      Object.fromEntries(
        (vocabulary?.block_actions ?? []).map((action) => [
          action.value,
          action.label,
        ])
      ),
    [vocabulary]
  )

  // Writes would run as the real admin while spoofing (useRequest drops spoofUserID on them).
  const availableActions = (changeRequest) => {
    if (isSpoofing) return []
    return reviewActions({ changeRequest, currentUserId, permissions })
  }

  // A dialog for an action the reloaded request no longer offers would act on a state nobody saw.
  useEffect(() => {
    if (!detail || !dialog) return
    if (!availableActions(detail.change_request).includes(dialog)) {
      setDialog(null)
    }
  })

  const shownOperationsHash = useRef(null)
  const operationsHash = detail?.change_request.operations_hash ?? null
  useEffect(() => {
    if (operationsHash === null) return
    if (
      shownOperationsHash.current !== null &&
      shownOperationsHash.current !== operationsHash
    ) {
      setDialog((open) => (open === 'approve' ? null : open))
    }
    shownOperationsHash.current = operationsHash
  }, [operationsHash])

  // A refused action usually means the request or its warnings moved on, so the page re-reads it.
  // A 409 means the operations moved on, so any open dialog is closed rather than left armed.
  const run = (action, body) => {
    const sentId = id
    setIsBusy(true)
    return api
      .action(id, action, body, { onConflict: () => setDialog(null) })
      .then((data) => {
        if (currentId.current !== sentId) return null
        if (data) {
          setDetail(data)
          setDialog(null)
        } else {
          setLoadAttempt((attempt) => attempt + 1)
        }
        return data
      })
      .finally(() => setIsBusy(false))
  }

  // The retry answers 200 whether or not DS accepted the clear, so the outcome is read off the detail.
  const retryCacheClear = () =>
    run('retry_cache_clear').then((data) => {
      if (!data) return
      const cleared = Boolean(data.change_request.cache_cleared_at)
      dispatch(
        setNotification({
          type: cleared ? 'success' : 'error',
          body: cleared
            ? 'DS cache cleared.'
            : 'The DS cache clear failed again; try later.',
        })
      )
    })

  const approve = (body) =>
    run('approve', {
      ...body,
      operations_hash: detail.change_request.operations_hash,
    })

  const opensDialog = (action) =>
    action === 'approve' || action === 'discard' || COMMENT_DIALOGS[action]
  const onAction = (action) => {
    if (action === 'edit') navi(`/lead_funnel_blocks?cr=${id}`)
    else if (opensDialog(action)) setDialog(action)
    else if (action === 'retry_cache_clear') retryCacheClear()
    else run(action)
  }

  if (!detail) {
    const placeholder = loadFailed ? (
      <div className="has-text-centered p-5" data-testid="cr-load-error">
        <p className="has-text-danger mb-2">
          The change request could not be loaded.
        </p>
        <button
          type="button"
          className="button is-small"
          onClick={() => setLoadAttempt((attempt) => attempt + 1)}
          data-testid="cr-load-retry"
        >
          Retry
        </button>
      </div>
    ) : (
      <Loading />
    )
    return <LayoutSidebar contentTitle="Change request" content={placeholder} />
  }

  const { change_request: changeRequest } = detail
  const meta = statusMeta(changeRequest.status)
  const actions = availableActions(changeRequest)

  const groups = groupCardsByTreatment({ cards: detail.diff_cards, nodes })

  const buttons = actions
    .filter((action) => ACTION_BUTTONS[action])
    .sort((a, b) => ACTION_BUTTONS[a].rank - ACTION_BUTTONS[b].rank)
  const isEmpty = detail.diff_cards.length === 0

  const content = (
    <div className="lox-block-change lox-cr-review">
      <header className="lox-cr-review__head">
        <div className="lox-cr-review__title-block">
          <Link
            className="lox-cr-review__back"
            to={`/lead_funnel_block_change_requests${
              location.state?.listSearch ?? ''
            }`}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            All change requests
          </Link>
          <div className="lox-cr-review__title-row">
            <h2 className="lox-cr-review__title" data-testid="cr-title">
              CR-{changeRequest.id} {changeRequest.title}
            </h2>
            <span
              className={`tag lox-no-border lox-cr-review__status ${meta.tagClass}`}
              data-testid="cr-status"
            >
              {meta.label}
            </span>
          </div>
          {changeRequest.description && (
            <p className="lox-cr-review__description">
              {changeRequest.description}
            </p>
          )}
        </div>
        <div className="buttons lox-cr-review__actions">
          {isSpoofing && (
            <p className="is-size-7 has-text-grey" data-testid="cr-read-only">
              Read-only while viewing as another user
            </p>
          )}
          {buttons.map((action) => (
            <button
              key={action}
              type="button"
              className={`button ${ACTION_BUTTONS[action].className}`}
              disabled={isBusy || (action === 'submit' && isEmpty)}
              title={
                action === 'submit' && isEmpty
                  ? 'Add at least one change before submitting.'
                  : undefined
              }
              onClick={() => onAction(action)}
              data-testid={`cr-action-${action}`}
            >
              <FontAwesomeIcon icon={ACTION_BUTTONS[action].icon} />
              <span>{ACTION_BUTTONS[action].label}</span>
            </button>
          ))}
        </div>
      </header>
      {changeRequest.status === 'applied' &&
        !changeRequest.cache_cleared_at && (
          <div
            className="notification is-warning is-light py-2"
            data-testid="cr-cache-pending"
          >
            Applied, but the DS cache clear failed. Live within 1 hour, or retry
            the cache clear.
          </div>
        )}

      <section className="lox-cr-review__card lox-cr-review__card--summary">
        <ChangeRequestSummary
          changeRequest={changeRequest}
          cards={detail.diff_cards}
          groups={groups}
          validation={detail.validation}
          isValidated={meta.isOpen}
        />
      </section>

      <section className="lox-cr-review__card">
        <h3 className="lox-cr-review__card-title">Changes</h3>
        <ChangeRequestChangesTable
          groups={groups}
          changeRequest={changeRequest}
          operations={detail.operations}
          validation={detail.validation}
          actionLabels={actionLabels}
          treatmentNamesById={treatmentNamesById}
        />
      </section>

      <div className="lox-cr-review__panels">
        <section className="lox-cr-review__card">
          <div className="lox-cr-review__card-head">
            <h3 className="lox-cr-review__card-title">
              <FontAwesomeIcon icon={faShieldHalved} />
              Validation
            </h3>
            {meta.isOpen && <ValidationPills validation={detail.validation} />}
          </div>
          <ChangeRequestValidation
            validation={detail.validation}
            isValidated={meta.isOpen}
          />
        </section>
        <section className="lox-cr-review__card">
          <div className="lox-cr-review__card-head">
            <h3 className="lox-cr-review__card-title">
              <FontAwesomeIcon icon={faComments} />
              Discussion
            </h3>
          </div>
          <ChangeRequestDiscussion
            events={detail.events}
            canComment={actions.includes('comment')}
            isBusy={isBusy}
            onComment={(comment) => run('comments', { comment })}
          />
        </section>
      </div>

      {dialog === 'approve' && (
        <ApproveDialog
          validation={detail.validation}
          operationsHash={changeRequest.operations_hash}
          isBusy={isBusy}
          onConfirm={approve}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'discard' && (
        <ConfirmDialog
          title="Discard draft"
          message="Discard this draft? This cannot be undone."
          confirmLabel="Discard"
          isBusy={isBusy}
          onConfirm={() => run('discard')}
          onClose={() => setDialog(null)}
        />
      )}
      {COMMENT_DIALOGS[dialog] && (
        <CommentDialog
          {...COMMENT_DIALOGS[dialog]}
          isBusy={isBusy}
          onConfirm={(body) => run(dialog, body)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )

  return <LayoutSidebar contentTitle="Change request" content={content} />
}
